// IndexedDB wrapper for VOANH AI
// Stores: chats, agents, settings, global_memory

const DB_NAME = 'ynx_db';
const DB_VERSION = 1;
const STORES = ['chats', 'agents', 'settings', 'global_memory'];

let _db = null;

export const openDB = () => {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      STORES.forEach((name) => {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'id' });
        }
      });
    };
    req.onsuccess = () => { _db = req.result; resolve(_db); };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB blocked (another tab has it open)'));
  });
};

/**
 * Hard-reset the local store. Use this when the database is corrupted or
 * grew too big and the app gets stuck on load. Deletes ALL chats, agents,
 * memory, and settings — but the user is back to a working app immediately.
 */
export const hardResetDB = async () => {
  try {
    if (_db) { try { _db.close(); } catch (err) { console.debug('YNX db: close already done', err); } _db = null; }
    await new Promise((resolve, reject) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = resolve;
      req.onerror = () => reject(req.error);
      req.onblocked = () => resolve(); // proceed anyway; will retry on next load
      setTimeout(resolve, 3000); // safety timeout
    });
    // Also clear any cached app state
    try { localStorage.clear(); } catch (err) { console.debug('YNX db: localStorage clear failed (private mode?)', err); }
    return true;
  } catch (e) {
    console.warn('YNX: hardResetDB failed', e);
    return false;
  }
};

const tx = async (store, mode = 'readonly') => {
  const db = await openDB();
  return db.transaction(store, mode).objectStore(store);
};

export const db = {
  async get(store, id) {
    const s = await tx(store);
    return new Promise((res, rej) => {
      const r = s.get(id);
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
  },
  async getAll(store) {
    const s = await tx(store);
    return new Promise((res, rej) => {
      const r = s.getAll();
      r.onsuccess = () => res(r.result || []);
      r.onerror = () => rej(r.error);
    });
  },
  async put(store, value) {
    const s = await tx(store, 'readwrite');
    return new Promise((res, rej) => {
      const r = s.put(value);
      r.onsuccess = () => res(value);
      r.onerror = () => rej(r.error);
    });
  },
  async delete(store, id) {
    const s = await tx(store, 'readwrite');
    return new Promise((res, rej) => {
      const r = s.delete(id);
      r.onsuccess = () => res(true);
      r.onerror = () => rej(r.error);
    });
  },
  async clear(store) {
    const s = await tx(store, 'readwrite');
    return new Promise((res, rej) => {
      const r = s.clear();
      r.onsuccess = () => res(true);
      r.onerror = () => rej(r.error);
    });
  },
};

// Convenience helpers
export const getSetting = async (key, fallback = null) => {
  const v = await db.get('settings', key);
  return v ? v.value : fallback;
};
export const setSetting = (key, value) => db.put('settings', { id: key, value });

export const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
